<template>
	<PrecoaModal
		:open="modelValue"
		size="medium"
		@update:open="handleUpdateOpen"
	>
		<template #default>
			<div class="p-2">
				<div class="text-md text-secondary-900 pb-6 text-center font-semibold">
					{{ t('paymentOptionsSlide.healthQuestions') }}
				</div>
				<ol
					class="marker:text-primary-700 list-decimal space-y-3 pb-6 pl-5 text-slate-700 marker:font-semibold"
				>
					<li
						v-for="(questions, index) in formattedHealthQuestions"
						:key="index"
						class="text-sm"
						v-html="questions"
					></li>
				</ol>

				<template v-if="showToggles">
					<div
						v-for="(plan, index) in plans"
						:key="plan.id"
						class="flex justify-center pb-4 last-of-type:pb-0"
					>
						<div
							class="flex items-center gap-8 pb-4"
							:class="{
								'border-b border-slate-200': index < (plans?.length ?? 0) - 1,
							}"
						>
							<PlanAvatar
								:plan="plan"
								:index="index"
								filled
							/>

							<div class="flex items-center gap-8">
								<BaseToggle
									v-model="plan.insurance.terminallyIll"
									:label="t('payment.term.terminalIllness')"
									leftToggle
									capitalize
								/>

								<BaseToggle
									v-model="plan.insurance.otherIllness"
									:label="t('payment.term.otherIllness')"
									leftToggle
									capitalize
								/>
							</div>
						</div>
					</div>
				</template>
			</div>
		</template>
	</PrecoaModal>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import BaseToggle from '@/components/common/BaseToggle.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { usePresentationLocale } from '@/i18n'
import { useHealthQuestions } from '@/mixins/healthQuestions'
import { useSessionStore } from '@/stores/session'
import { computed } from 'vue'

const { t } = usePresentationLocale()
const sessionStore = useSessionStore()
const { getHealthQuestions } = useHealthQuestions()

withDefaults(
	defineProps<{
		modelValue: boolean
		plans?: Plan[]
		showToggles?: boolean
	}>(),
	{
		showToggles: false,
	}
)

const emit = defineEmits(['update:modelValue'])

const handleUpdateOpen = (val: boolean | undefined) =>
	emit('update:modelValue', val ?? false)

const state = computed(() => sessionStore.activeFh?.billingAddress?.stateCode)

const stateHealthQuestions = computed(() => getHealthQuestions(state.value))

const formattedHealthQuestions = computed(() =>
	stateHealthQuestions.value.map((questions: string) =>
		questions
			.replace(
				/REQUIRED:/g,
				'<span class="font-semibold text-secondary-900">REQUIRED:</span>'
			)
			.replace(
				/\bOR\b/g,
				'<br><br><span class="font-semibold text-secondary-900">OR</span>'
			)
	)
)
</script>
